import { ChangeDetectorRef, Component, Inject, Input } from '@angular/core';
import { UntypedFormArray } from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

import {firstValueFrom, forkJoin} from 'rxjs';
import { filter, first, map, switchMap, takeUntil } from 'rxjs/operators';

import { BaseCtrlValueAccessorFormArrayComponent, FormManagerService } from '@sim/form';
import { Media, UIMediaService } from '@sim/media';
import { ImagePickerDialogComponent } from '@sim/media/dialogs/image-picker-dialog/image-picker-dialog.component';
import { MediaService } from '@sim/media/services/media.service';
import { ClassName } from '@sim/role';

import { filePath, SwalService } from '@atmi/core';
import { Attachment, AttachmentMeta } from '@atmi/core/models';

@Component({
  selector: 'app-attachments-manager',
  styleUrls: ['./attachments-manager.component.scss'],
  templateUrl: './attachments-manager.component.html',
})
export class AttachmentsManagerComponent extends BaseCtrlValueAccessorFormArrayComponent<Attachment> {

  @Input() classNames: ClassName[] = [];

  readonly extendedAttachmentMedia$ = this.formValue$.pipe(
    filter((form): form is UntypedFormArray => !!form),
    switchMap((form) => {
      const attachmentsIds: string[] = form.value.map((v: Attachment) => v.file);
      const attachmentsPromises = attachmentsIds.map((id) => this.mediaApi.get(id).pipe(first()));

      return attachmentsPromises.length > 0
        ? forkJoin(attachmentsPromises)
        : Promise.resolve([]);
    }),
    map((media) => {
      if (!this.form) {
        throw new Error('Cannot set bibliographic list without a form');
      }

      const attachments: Attachment[] = this.form.value;
      return attachments.map((source) => {
        const file = media.find((m) => m.id === source.file);
        return {
          ...source,
          file: {
            ...file,
            previewUrl: file ? this.fileUrl + file.previewUrl : undefined,
          },
        };
      });
    }),
    takeUntil(this.unsubscription$),
  );

  constructor(
    @Inject(filePath) private readonly fileUrl: string,
    private readonly cd: ChangeDetectorRef,
    private readonly dialog: MatDialog,
    private readonly formManager: FormManagerService,
    private readonly mediaApi: MediaService,
    private readonly swal: SwalService,
    private readonly translate: TranslateService,
    private readonly uiMediaService: UIMediaService,
  ) {
    super();
  }

  orderAttachmentHandler(items: Array<Attachment & { file: { id: string } }>) {
    this.value = items.map((i, index) => ({
      descrizione: i.descrizione,
      file: i.file.id,
      ordine: index + 1,
      tipo: i.tipo,
    }));
  }

  showMediaHandler(item: Attachment & { file: Media & { id: string } }) {
    this.uiMediaService.dispatchViewMediaAction(item.file);
  }

  async addAttatchmentHandler(allMedia: Attachment[]) {
    const order = allMedia.length > 0
      ? allMedia[allMedia.length - 1].ordine
      : 0;

    const ref = this.dialog.open(ImagePickerDialogComponent, { data: {}, width: '80vw', maxHeight: '95vh' });
    const res: Array<Media & { id: string }> | undefined = await firstValueFrom(ref.afterClosed().pipe(first()));

    if (res && this.form) {
      // TODO: Remove embedded 'tipo'
      const attachments: Attachment[] = res.map((m, index) => ({ file: m.id, ordine: order + index + 1, tipo: 'immagine' }));

      attachments.forEach((a) => {
        const group = this.formManager.makeForm(AttachmentMeta, a);
        if (this.form) {
          this.form.push(group);
        }
      });

      this.value = [...this.form.value]; // TODO: Remove this
      this.cd.markForCheck();
    }
  }

  async removeAttachmentHandler(index: number) {
    const res = await this.swal.showConfirm({
      text: this.translate.instant('Sei sicuro di voler rimuovere questo allegato?'),
      title: this.translate.instant('Rimozione allegato'),
    });

    if (res) {
      if (res && this.form) {
        this.form.removeAt(index);
        // re order attachment after delete one
        this.value = this.form.value.map((a: Attachment, i: number) => ({
          ...a,
          ordine: i + 1,
        }));

        this.cd.markForCheck();
      }
    }
  }
}
